import LocalizedStrings from "react-localization";
import { useEffect, useState } from "react";
import { Grid } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import toast from "react-hot-toast";
import { CCard } from "@coreui/react";

import LoaderFullScreen from "components/utils/LoaderFullScreen";
import {
  getServiceOrder,
  serviceOrderLogsByServiceOrderID,
} from "graphql/queries";
import ServiceOrderLog from "components/serviceOrders/ServiceOrderLog";
import { getUserEmail } from "graphql/custom-queries";
import ServiceOrderLogSimple from "components/serviceOrders/ServiceOrderLogSimple";
import { serviceOrderStatus } from "config/constants";
import { updateServiceOrderLog } from "graphql/mutations";

const ServiceOrderLogs = ({ serviceOrderID, simple }) => {
  const [serviceOrderLogs, setServiceOrderLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  async function fetchUserEmail(id) {
    try {
      const userData = await API.graphql(
        graphqlOperation(getUserEmail, {
          id,
        })
      );
      const userItem = userData.data.getUser;
      return userItem?.email ?? "";
    } catch (err) {
      console.log("Error fetchUserEmail", err);
      return "";
    }
  }

  const getNamesByServiceOrders = (list) => {
    list?.forEach(async (item) => {
      const userName = item.userID ? await fetchUserEmail(item.userID) : "";
      const assignedExecutorName = item.assignedExecutor
        ? await fetchUserEmail(item.assignedExecutor)
        : "";

      setServiceOrderLogs((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          userName,
          assignedExecutorName,
        };

        return updatedList;
      });
    });
  };

  async function fetchServiceOrdersLogs(id) {
    try {
      setLoading(true);

      const serviceOrderLogs = await API.graphql(
        graphqlOperation(serviceOrderLogsByServiceOrderID, {
          serviceOrderID: id,
          sortDirection: "DESC",
        })
      );
      let serviceOrderLogsItems =
        serviceOrderLogs.data.serviceOrderLogsByServiceOrderID.items;

      const canEditDates = await checkEditDates(id);

      if (canEditDates)
        serviceOrderLogsItems = serviceOrderLogsItems.map((item) => ({
          ...item,
          canEdit: true,
        }));

      setServiceOrderLogs(serviceOrderLogsItems);
      getNamesByServiceOrders(serviceOrderLogsItems);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setLoading(false);
    }
  }

  async function checkEditDates(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrder, { id })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      if (!serviceOrderItem) return;

      return (
        serviceOrderItem.equipments?.items?.length === 1 &&
        serviceOrderItem.status !== serviceOrderStatus.scheduled
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      return false;
    }
  }

  async function updateLogDate(id, date) {
    try {
      await API.graphql(
        graphqlOperation(updateServiceOrderLog, {
          input: {
            id,
            createdAt: new Date(date),
          },
        })
      );
    } catch (err) {
      console.log("Error update log date", err);
    }
  }

  const onChangeDate = (id, date) => {
    updateLogDate(id, date);
    setServiceOrderLogs((prevLogs) =>
      prevLogs.map((log) => (log.id === id ? { ...log, createdAt: date } : log))
    );
  };

  useEffect(() => {
    fetchServiceOrdersLogs(serviceOrderID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderID]);

  return (
    <>
      {!simple ? (
        <Grid gap="15px">
          {serviceOrderLogs?.map((serviceOrderLog) => (
            <ServiceOrderLog
              item={serviceOrderLog}
              onChangeDate={onChangeDate}
            />
          ))}
        </Grid>
      ) : (
        <CCard
          className="p-3"
          borderRadius="medium"
          variation="outlined"
          style={{ height: "100%" }}
        >
          <Grid gap="15px">
            <span className="fs-5">{strings.titleServiceOrderLog}</span>
            <Grid templateColumns={{ base: "1fr 1fr 1fr" }} gap="15px">
              {serviceOrderLogs?.map((serviceOrderLog) => (
                <ServiceOrderLogSimple
                  className="avoidBreak"
                  item={serviceOrderLog}
                  onChangeDate={onChangeDate}
                />
              ))}
            </Grid>
          </Grid>
        </CCard>
      )}
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    titleServiceOrderLog: "Resumen de trazabilidad",
    responses: {
      errorFetch: "Error al consultar datos",
    },
  },
});

export default ServiceOrderLogs;
